import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 glass backdrop-blur-sm",
  {
    variants: {
      variant: {
        default: "border border-white/20 text-white hover:bg-white/10 hover:border-white/30",
        destructive: "border border-red-500/30 text-red-400 hover:bg-red-500/20 hover:border-red-500/50 hover:shadow-red-500/20 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]",
        outline: "border border-white/20 text-white hover:bg-white/5 hover:border-white/30",
        secondary: "bg-white/5 text-white border border-white/10 hover:bg-white/10 hover:border-white/20",
        ghost: "text-white hover:bg-white/10 hover:text-white border-transparent",
        link: "text-primary underline-offset-4 hover:underline border-transparent bg-transparent",
        action: "border border-orange-500/30 text-orange-400 hover:bg-orange-500/20 hover:border-orange-500/50 hover:shadow-orange-500/20 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] font-medium",
        success: "border border-green-500/30 text-green-400 hover:bg-green-500/20 hover:border-green-500/50 hover:shadow-green-500/20 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] font-medium",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
